/** @module @liquidefy/code-editor/editor/editor-find @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  Selection,
  FindState,
  FindMatch,
  FindInFilesState,
  FileSearchResult,
  DecorationData,
  DecorationId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asDecorationId,
  generateId,
  clamp,
  comparePositions,
  positionToOffset,
  offsetToPosition,
} from '../code-editor.js';

// ═══ §2 FIND — create state, search, regex construction, highlighting ═══

/**
 * Creates a FindState with defaults, optionally overridden.
 */
export function createFindState(
  options?: Partial<FindState>,
): EditorResult<FindState> {
  const state: FindState = {
    searchString: options?.searchString ?? '',
    replaceString: options?.replaceString ?? '',
    isRegex: options?.isRegex ?? false,
    isCaseSensitive: options?.isCaseSensitive ?? false,
    isWholeWord: options?.isWholeWord ?? false,
    searchScope: options?.searchScope ?? null,
    matchCount: 0,
    currentMatch: 0,
    preserveCase: options?.preserveCase ?? false,
  };

  return ok(state, 'FIND_STATE_CREATED', 'Find state initialized');
}

/**
 * Finds all matches of the search pattern in the content.
 */
export function findAll(
  content: string,
  state: FindState,
): EditorResult<FindMatch[]> {
  if (state.searchString.length === 0) {
    return ok([], 'EMPTY_SEARCH', 'Empty search string');
  }

  const regexResult = buildRegex(state);
  if (!regexResult.success) return err(regexResult.code, regexResult.message);
  const regex = regexResult.value;

  const matches: FindMatch[] = [];
  let match: RegExpExecArray | null;
  const scopeContent = scopedContent(content, state.searchScope);
  const scopeOffset = state.searchScope ? positionToOffset(content, state.searchScope.start) : 0;

  // Reset regex state
  regex.lastIndex = 0;

  while ((match = regex.exec(scopeContent)) !== null) {
    if (match[0].length === 0) {
      regex.lastIndex++;
      continue;
    }

    const startPos = offsetToPosition(content, match.index + scopeOffset);
    const endPos = offsetToPosition(content, match.index + scopeOffset + match[0].length);

    matches.push({
      range: { start: startPos, end: endPos },
      matches: [...match],
    });

    // Safety limit
    if (matches.length >= 10000) break;
  }

  return ok(matches, 'FIND_ALL', `Found ${matches.length} matches`);
}

/**
 * Finds the next match after the given position.
 */
export function findNext(
  content: string,
  state: FindState,
  fromPosition: Position,
): EditorResult<FindMatch | null> {
  const allResult = findAll(content, state);
  if (!allResult.success) return err(allResult.code, allResult.message);

  const matches = allResult.value;
  if (matches.length === 0) return ok(null, 'NO_MATCH', 'No matches found');

  const next = matches.find((m) => comparePositions(m.range.start, fromPosition) > 0);
  // Wrap around if needed
  return ok(next ?? matches[0]!, 'FIND_NEXT', 'Found next match');
}

/**
 * Finds the previous match before the given position.
 */
export function findPrevious(
  content: string,
  state: FindState,
  fromPosition: Position,
): EditorResult<FindMatch | null> {
  const allResult = findAll(content, state);
  if (!allResult.success) return err(allResult.code, allResult.message);

  const matches = allResult.value;
  if (matches.length === 0) return ok(null, 'NO_MATCH', 'No matches found');

  // Find the last match before fromPosition
  let prev: FindMatch | null = null;
  for (const m of matches) {
    if (comparePositions(m.range.start, fromPosition) < 0) {
      prev = m;
    }
  }

  // Wrap around
  return ok(prev ?? matches[matches.length - 1]!, 'FIND_PREV', 'Found previous match');
}

/**
 * Finds matches within a specific range.
 */
export function findInRange(
  content: string,
  state: FindState,
  range: Range,
): EditorResult<FindMatch[]> {
  const scopedState: FindState = { ...state, searchScope: range };
  return findAll(content, scopedState);
}

/**
 * Constructs a RegExp from the FindState options.
 */
export function buildRegex(state: FindState): EditorResult<RegExp> {
  if (state.searchString.length === 0) {
    return err('EMPTY_PATTERN', 'Search string is empty');
  }

  let pattern = state.searchString;

  if (!state.isRegex) {
    // Escape regex special characters for literal search
    pattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  if (state.isWholeWord) {
    pattern = `\\b${pattern}\\b`;
  }

  const flags = state.isCaseSensitive ? 'g' : 'gi';

  try {
    return ok(new RegExp(pattern, flags), 'REGEX_BUILT', 'Regex constructed');
  } catch (e) {
    return err('INVALID_REGEX', `Invalid regex: ${String(e)}`);
  }
}

/**
 * Returns the number of matches in the content.
 */
export function matchCount(content: string, state: FindState): number {
  const result = findAll(content, state);
  if (!result.success) return 0;
  return result.value.length;
}

/**
 * Creates decoration data for highlighting all matches.
 */
export function highlightMatches(matches: FindMatch[]): DecorationData[] {
  return matches.map((m, i) => ({
    id: asDecorationId(generateId('fm')),
    range: m.range,
    options: {
      className: 'find-match',
      inlineClassName: 'find-match-inline',
      stickiness: 'never-grows' as const,
      overviewRulerColor: '#d186167e',
      minimap: { color: '#d186167e', position: 'inline' as const },
      isWholeLine: false,
    },
  }));
}

// ═══ §3 REPLACE — single, all, in selection, case preservation ═══

/**
 * Replaces a single match in the content.
 */
export function replaceOne(
  content: string,
  match: FindMatch,
  replacement: string,
  preserveCase: boolean = false,
): EditorResult<string> {
  const startOffset = positionToOffset(content, match.range.start);
  const endOffset = positionToOffset(content, match.range.end);

  const original = content.substring(startOffset, endOffset);
  const finalReplacement = preserveCase
    ? preserveCaseTransform(original, replacement)
    : buildReplacement(match, replacement, false);

  const result = content.substring(0, startOffset) + finalReplacement + content.substring(endOffset);
  return ok(result, 'REPLACE_ONE', 'Replaced one match');
}

/**
 * Replaces all matches in the content.
 * Processes matches in reverse order to preserve offset correctness.
 */
export function replaceAll(
  content: string,
  matches: FindMatch[],
  replacement: string,
  preserveCase: boolean = false,
): EditorResult<string> {
  if (matches.length === 0) return ok(content, 'NO_MATCHES', 'No matches to replace');

  // Process in reverse order so offsets remain valid
  const sorted = [...matches].sort(
    (a, b) => comparePositions(b.range.start, a.range.start),
  );

  let result = content;
  for (const match of sorted) {
    const replaceResult = replaceOne(result, match, replacement, preserveCase);
    if (!replaceResult.success) return replaceResult;
    result = replaceResult.value;
  }

  return ok(result, 'REPLACE_ALL', `Replaced ${matches.length} matches`);
}

/**
 * Replaces matches only within the given selection range.
 */
export function replaceInSelection(
  content: string,
  selection: Selection,
  state: FindState,
  replacement: string,
): EditorResult<string> {
  const selRange: Range = {
    start: comparePositions(selection.anchor, selection.active) <= 0 ? selection.anchor : selection.active,
    end: comparePositions(selection.anchor, selection.active) > 0 ? selection.anchor : selection.active,
  };

  const scopedState: FindState = { ...state, searchScope: selRange };
  const matchResult = findAll(content, scopedState);
  if (!matchResult.success) return err(matchResult.code, matchResult.message);

  return replaceAll(content, matchResult.value, replacement, state.preserveCase);
}

/**
 * Builds the replacement string, handling regex group references ($1, $2, etc.).
 */
export function buildReplacement(
  match: FindMatch,
  replacement: string,
  preserveCase: boolean,
): string {
  let result = replacement;

  // Replace group references $0, $1, $2, etc.
  result = result.replace(/\$(\d+)/g, (_full, groupNum) => {
    const idx = parseInt(groupNum, 10);
    return match.matches[idx] ?? '';
  });

  // Replace named group references ${name}
  result = result.replace(/\$\{(\w+)\}/g, (_full, name) => {
    // Named groups are not in our simple matches array, return empty
    return '';
  });

  // Special replacement patterns
  result = result.replace(/\\n/g, '\n');
  result = result.replace(/\\t/g, '\t');
  result = result.replace(/\\\\/g, '\\');

  if (preserveCase) {
    result = preserveCaseTransform(match.matches[0] ?? '', result);
  }

  return result;
}

/**
 * Transforms the replacement string to match the casing of the original text.
 * - all uppercase -> replacement all uppercase
 * - all lowercase -> replacement all lowercase
 * - first char uppercase -> replacement first char uppercase
 * - mixed -> keep replacement as-is
 */
export function preserveCaseTransform(original: string, replacement: string): string {
  if (original.length === 0 || replacement.length === 0) return replacement;

  // All uppercase
  if (original === original.toUpperCase() && original !== original.toLowerCase()) {
    return replacement.toUpperCase();
  }

  // All lowercase
  if (original === original.toLowerCase() && original !== original.toUpperCase()) {
    return replacement.toLowerCase();
  }

  // Title case (first char uppercase, rest lowercase)
  if (
    original[0] === original[0]!.toUpperCase() &&
    original.substring(1) === original.substring(1).toLowerCase()
  ) {
    return replacement[0]!.toUpperCase() + replacement.substring(1).toLowerCase();
  }

  // Mixed case — keep as-is
  return replacement;
}

// ═══ §4 FIND IN FILES — multi-file search, aggregation, filtering ═══

/**
 * Creates a FindInFilesState with defaults.
 */
export function createFindInFilesState(
  options?: Partial<FindInFilesState>,
): EditorResult<FindInFilesState> {
  const state: FindInFilesState = {
    query: options?.query ?? '',
    include: options?.include ?? '',
    exclude: options?.exclude ?? '',
    isRegex: options?.isRegex ?? false,
    isCaseSensitive: options?.isCaseSensitive ?? false,
    isWholeWord: options?.isWholeWord ?? false,
    results: options?.results ?? [],
  };

  return ok(state, 'FIND_IN_FILES_CREATED', 'Find in files state initialized');
}

/**
 * Searches for matches in a single file's content.
 */
export function matchInFile(
  content: string,
  uri: string,
  state: FindInFilesState,
): EditorResult<FileSearchResult> {
  const findState: FindState = {
    searchString: state.query,
    replaceString: '',
    isRegex: state.isRegex,
    isCaseSensitive: state.isCaseSensitive,
    isWholeWord: state.isWholeWord,
    searchScope: null,
    matchCount: 0,
    currentMatch: 0,
    preserveCase: false,
  };

  const matchResult = findAll(content, findState);
  if (!matchResult.success) return err(matchResult.code, matchResult.message);

  const lines = content.split('\n');

  return ok({
    uri,
    matches: matchResult.value,
    lineCount: lines.length,
  }, 'FILE_SEARCHED', `Found ${matchResult.value.length} matches in ${uri}`);
}

/**
 * Aggregates multiple file search results.
 */
export function aggregateResults(
  results: FileSearchResult[],
): { totalMatches: number; totalFiles: number; filesWithMatches: number } {
  let totalMatches = 0;
  let filesWithMatches = 0;

  for (const r of results) {
    totalMatches += r.matches.length;
    if (r.matches.length > 0) filesWithMatches++;
  }

  return {
    totalMatches,
    totalFiles: results.length,
    filesWithMatches,
  };
}

/**
 * Filters search results by include/exclude glob patterns (simplified matching).
 */
export function filterResults(
  results: FileSearchResult[],
  include: string,
  exclude: string,
): FileSearchResult[] {
  let filtered = results;

  if (include.length > 0) {
    const includePatterns = include.split(',').map((p) => p.trim());
    filtered = filtered.filter((r) =>
      includePatterns.some((pattern) => simpleGlobMatch(r.uri, pattern)),
    );
  }

  if (exclude.length > 0) {
    const excludePatterns = exclude.split(',').map((p) => p.trim());
    filtered = filtered.filter((r) =>
      !excludePatterns.some((pattern) => simpleGlobMatch(r.uri, pattern)),
    );
  }

  return filtered;
}

/**
 * Sorts search results by the specified criterion.
 */
export function sortResults(
  results: FileSearchResult[],
  by: 'file' | 'matches' | 'relevance',
): FileSearchResult[] {
  switch (by) {
    case 'file':
      return [...results].sort((a, b) => a.uri.localeCompare(b.uri));
    case 'matches':
      return [...results].sort((a, b) => b.matches.length - a.matches.length);
    case 'relevance':
      // Relevance: files with more matches and shorter paths rank higher
      return [...results].sort((a, b) => {
        const aScore = a.matches.length * 10 - a.uri.length;
        const bScore = b.matches.length * 10 - b.uri.length;
        return bScore - aScore;
      });
    default:
      return results;
  }
}

// ═══ §5 SEARCH NAVIGATION — cycle through matches, position queries ═══

/**
 * Advances to the next match, wrapping if at the end.
 */
export function nextMatch(state: FindState): FindState {
  if (state.matchCount === 0) return state;
  const next = (state.currentMatch + 1) % state.matchCount;
  return { ...state, currentMatch: next };
}

/**
 * Goes back to the previous match, wrapping if at the start.
 */
export function previousMatch(state: FindState): FindState {
  if (state.matchCount === 0) return state;
  const prev = (state.currentMatch - 1 + state.matchCount) % state.matchCount;
  return { ...state, currentMatch: prev };
}

/**
 * Returns the match at the given position, or null.
 */
export function matchAtPosition(
  matches: FindMatch[],
  position: Position,
): FindMatch | null {
  return matches.find((m) => {
    if (comparePositions(position, m.range.start) < 0) return false;
    if (comparePositions(position, m.range.end) > 0) return false;
    return true;
  }) ?? null;
}

/**
 * Returns the nearest match to the given position.
 */
export function nearestMatch(
  matches: FindMatch[],
  position: Position,
): FindMatch | null {
  if (matches.length === 0) return null;

  let nearest = matches[0]!;
  let nearestDistance = positionDistance(position, nearest.range.start);

  for (let i = 1; i < matches.length; i++) {
    const dist = positionDistance(position, matches[i]!.range.start);
    if (dist < nearestDistance) {
      nearest = matches[i]!;
      nearestDistance = dist;
    }
  }

  return nearest;
}

/**
 * Returns the index of a match in the matches array.
 */
export function matchIndex(matches: FindMatch[], match: FindMatch): number {
  return matches.findIndex(
    (m) =>
      m.range.start.line === match.range.start.line &&
      m.range.start.column === match.range.start.column,
  );
}

/**
 * Sets the current match index in the find state.
 */
export function setCurrentMatch(state: FindState, index: number): FindState {
  const clamped = clamp(index, 0, Math.max(0, state.matchCount - 1));
  return { ...state, currentMatch: clamped };
}

// ═══ §6 SEARCH HISTORY — maintain search query history ═══

/** Maximum number of entries to keep in search history. */
export const MAX_SEARCH_HISTORY = 50;

/**
 * Adds a query to the search history, removing duplicates and enforcing size limit.
 */
export function addToSearchHistory(history: string[], query: string): string[] {
  if (query.length === 0) return history;

  // Remove existing duplicate
  const filtered = history.filter((h) => h !== query);

  // Add to front
  const result = [query, ...filtered];

  // Enforce limit
  if (result.length > MAX_SEARCH_HISTORY) {
    return result.slice(0, MAX_SEARCH_HISTORY);
  }

  return result;
}

/**
 * Navigates up (older) in the search history.
 */
export function searchHistoryUp(history: string[], index: number): { query: string; index: number } {
  if (history.length === 0) return { query: '', index: -1 };
  const newIndex = clamp(index + 1, 0, history.length - 1);
  return { query: history[newIndex]!, index: newIndex };
}

/**
 * Navigates down (newer) in the search history.
 */
export function searchHistoryDown(history: string[], index: number): { query: string; index: number } {
  if (history.length === 0) return { query: '', index: -1 };
  const newIndex = index - 1;
  if (newIndex < 0) return { query: '', index: -1 };
  return { query: history[newIndex]!, index: newIndex };
}

// ═══ INTERNAL HELPERS ═══

/**
 * Extracts the content within a search scope.
 */
function scopedContent(content: string, scope: Range | null): string {
  if (!scope) return content;
  const startOffset = positionToOffset(content, scope.start);
  const endOffset = positionToOffset(content, scope.end);
  return content.substring(startOffset, endOffset);
}

/**
 * Computes a rough distance between two positions for nearest-match calculation.
 */
function positionDistance(a: Position, b: Position): number {
  const lineDelta = Math.abs(a.line - b.line);
  const colDelta = Math.abs(a.column - b.column);
  return lineDelta * 10000 + colDelta;
}

/**
 * Simple glob matching: supports * (any chars) and ? (single char).
 */
function simpleGlobMatch(text: string, pattern: string): boolean {
  // Convert glob to regex
  let regexStr = '^';
  for (const ch of pattern) {
    switch (ch) {
      case '*': regexStr += '.*'; break;
      case '?': regexStr += '.'; break;
      case '.': regexStr += '\\.'; break;
      default: regexStr += ch;
    }
  }
  regexStr += '$';

  try {
    return new RegExp(regexStr).test(text);
  } catch {
    return false;
  }
}
